import { useState, useEffect } from "react";
import { CATEGORIES, today } from "../constants/categories";
import { useToast } from "./Toast";

export default function ChallengeView({
  challenge, loading, done, selectedCat, setSelectedCat, catColor, streak,
  reflectionInput, setReflectionInput, reflection, loadingReflect,
  todayChallenges,
  onGenerate, onMarkDone, onGenerateReflection, onSaveReflection, onReset,
}) {
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  const [hoursLeft, setHoursLeft] = useState(null);
  const [showReflection, setShowReflection] = useState(false);
  useEffect(() => { setSaved(false); setShowReflection(false); }, [challenge?.title]);

  // Delay showing reflection so user can fully enjoy the confetti moment
  useEffect(() => {
    if (done && !showReflection) {
      const timer = setTimeout(() => {
        setShowReflection(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [done, showReflection]);

  const completedToday = (todayChallenges || []).filter(c => c.completed);

  // Streak warning: check every minute after 20h
  useEffect(() => {
    function checkTime() {
      const now = new Date();
      const hour = now.getHours();
      if (hour >= 20 && completedToday.length === 0 && streak.current_streak > 0) {
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        const h = Math.floor((midnight - now) / 3600000);
        const m = Math.floor(((midnight - now) % 3600000) / 60000);
        setHoursLeft(`${h}h${m > 0 ? ` ${m}m` : ""}`);
      } else {
        setHoursLeft(null);
      }
    }
    checkTime();
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [completedToday.length, streak.current_streak]);

  return (
    <>
      {/* Streak warning banner */}
      {hoursLeft && (
        <div className="status-banner status-banner-warning">
          <span style={{ fontSize: "20px" }}>⚠️</span>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#F59E0B" }}>
              Còn {hoursLeft} để giữ streak {streak.current_streak} ngày!
            </div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
              Hoàn thành 1 thử thách trước nửa đêm nhé 🔥
            </div>
          </div>
        </div>
      )}

      <div className="challenge-date">
        <div className="challenge-date-text">
          {today.toLocaleDateString("vi-VN", { weekday: "long", day: "numeric", month: "long" })}
        </div>
        {completedToday.length > 0 && !challenge && (
          <div className="challenge-completed-today">
            ✅ Đã hoàn thành {completedToday.length} thử thách hôm nay
          </div>
        )}
      </div>

      {/* Category Selector */}
      {!challenge && (
        <div style={{ marginBottom: "24px" }}>
          <div className="section-label">Chọn Lĩnh Vực</div>
          <div className="cat-grid">
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setSelectedCat(cat)} className="cat-btn"
                style={{
                  background: selectedCat.id === cat.id ? `${cat.color}15` : "var(--bg-card)",
                  border: `1px solid ${selectedCat.id === cat.id ? `${cat.color}88` : "var(--border-secondary)"}`,
                  color: selectedCat.id === cat.id ? cat.color : "var(--text-tertiary)",
                  boxShadow: selectedCat.id === cat.id ? `0 2px 8px ${cat.color}22` : "var(--card-shadow)",
                }}>
                <span className="cat-btn-emoji">{cat.emoji}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Generate */}
      {!challenge && (
        <button onClick={onGenerate} disabled={loading} className="btn-primary"
          style={{
            background: `linear-gradient(135deg, ${catColor}, ${catColor}BB)`,
            boxShadow: `0 4px 16px ${catColor}33`,
            marginBottom: "16px",
            cursor: loading ? "wait" : "pointer",
          }}>
          {loading ? <span>⟳ Đang tạo thử thách...</span> : "✨ Nhận Thử Thách Hôm Nay"}
        </button>
      )}

      {/* Challenge Card */}
      {challenge && (
        <div style={{ animation: "fadeIn 0.3s ease" }}>
          <div className="challenge-card" style={{ border: `1px solid ${catColor}44`, boxShadow: `0 0 40px ${catColor}18` }}>
            <div className="challenge-header">
              <span className="challenge-cat-emoji">{selectedCat.emoji}</span>
              <div>
                <div className="challenge-cat-label" style={{ color: catColor }}>{selectedCat.label}</div>
                <div className="challenge-title">{challenge.title}</div>
              </div>
              {done && <span style={{ marginLeft: "auto", fontSize: "24px" }}>✅</span>}
            </div>

            <div className="challenge-task">{challenge.task}</div>

            <div className="challenge-info-grid">
              <div className="challenge-info-box" style={{ background: `${catColor}11`, borderColor: `${catColor}33` }}>
                <div className="challenge-info-label" style={{ color: catColor }}>⏱ THỜI GIAN</div>
                <div style={{ fontSize: "15px", fontWeight: "600" }}>{challenge.time}</div>
              </div>
              <div className="challenge-info-box" style={{ background: "var(--bg-hover)", borderColor: "var(--border-primary)" }}>
                <div className="challenge-info-label" style={{ color: "var(--text-tertiary)" }}>💡 MẸO</div>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.5" }}>{challenge.tip}</div>
              </div>
            </div>

            <div className="challenge-why">
              <div className="challenge-why-label">🧠 TẠI SAO?</div>
              <div className="challenge-why-text">{challenge.why}</div>
            </div>

            {!done ? (
              <button onClick={onMarkDone} className="btn-done">✅ Đã Hoàn Thành!</button>
            ) : (
              <div className="done-banner">
                🎉 Xuất sắc! Streak: {streak.current_streak} ngày liên tiếp!
              </div>
            )}
          </div>

          {/* Reflection — appears after confetti moment */}
          {done && showReflection && (
            <div className={`reflection-section reflection-enter${saved || reflection ? " reflection-done" : ""}`}>
              <div className="reflection-label">📝 GHI LẠI CẢM NHẬN</div>
              {saved ? (
                <div className="done-banner">✅ Đã lưu cảm nhận!</div>
              ) : !reflection ? (
                <>
                  <textarea value={reflectionInput} onChange={e => setReflectionInput(e.target.value)}
                    placeholder="Bạn cảm thấy thế nào? Học được gì?"
                    className="reflection-textarea" />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => { onSaveReflection(); setSaved(true); toast("Đã lưu cảm nhận!"); }} disabled={!reflectionInput.trim()} className="btn-secondary">
                      💾 Lưu cảm nhận
                    </button>
                    <button onClick={onGenerateReflection} disabled={loadingReflect || !reflectionInput.trim()} className="btn-secondary">
                      {loadingReflect ? "⟳ Đang xử lý..." : "💬 Nhận phản hồi từ AI"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="reflection-ai-text">"{reflection}"</div>
                  <div className="done-banner">✅ Đã lưu cảm nhận & phản hồi</div>
                </>
              )}
            </div>
          )}

          <button onClick={onReset} className="btn-reset">🔄 Thử thách khác</button>
        </div>
      )}

      {!challenge && !loading && (
        <div className="motivation-quote">
          "Không cần hoàn hảo — chỉ cần tốt hơn hôm qua một chút."
        </div>
      )}

      {/* Completed challenges today */}
      {completedToday.length > 0 && (
        <div className="completed-list">
          <div className="section-label">
            🏆 Đã hoàn thành hôm nay ({completedToday.length})
          </div>
          {completedToday.map((c) => {
            const cat = CATEGORIES.find(ct => ct.id === c.category_id) || CATEGORIES[0];
            return (
              <div key={c.id} className="completed-item">
                <div className="completed-item-header">
                  <span style={{ fontSize: "20px" }}>{cat.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div className="completed-item-title">{c.title}</div>
                    <div className="completed-item-meta">{cat.label} • {c.time_required}</div>
                  </div>
                  <span style={{ fontSize: "16px" }}>✅</span>
                </div>
                {c.reflection && (
                  <div className="completed-item-reflection">📝 {c.reflection}</div>
                )}
                {c.ai_feedback && (
                  <div className="completed-item-reflection" style={{ color: "var(--text-muted)" }}>💬 {c.ai_feedback}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
